import React, {
  createContext,
  useState,
  useContext,
  ReactNode,
  useEffect,
  useRef,
} from "react";
import { Source, PipelineService, Banner } from "../services/PipelineService";
import {
  sendCloseClick,
  sendSearchClick,
  sendSearchOpened,
  sendSearchResponse,
} from "../utils/analyticsEvents";
import { trackAction } from "../utils/satisfactionSurvey";
import {
  sendContextConfirmation,
  sendContextRequest,
} from "../utils/contextEvents";
import { AnswerComponent, Tutorial } from "../services/AnswerComponentsService";
import { ActionButton } from "../services/AnswerComponentsService";
import { v4 as uuidv4 } from "uuid";
import { retry } from "../utils/retryFunction";
import productDescriptions from "../assets/productsDescriptions.json";

export interface Product {
  code: string;
  description: string;
}

interface ReceivedMessage {
  action: string;
  products: string[];
  origin: string;
  documentType: string;
  url?: string;
}

interface ProductDescription {
  code: string;
  description: string;
  documentTypes: string[];
  default: boolean;
}

type SearchOrigin = "initial" | "internal";
type SearchType =
  | "keyword"
  | "suggestion"
  | "related"
  | "drawer"
  | "menu"
  | "actionButton";

interface SearchContextType {
  textResponse: string;
  hasSearched: boolean;
  resetSearch: () => void;
  isLoading: boolean;
  isTextResponseComplete: boolean;
  currentSearchTerm: string;
  sources: Source[];
  banners: Banner[];
  actions: ActionButton[];
  tutorials: Tutorial[];
  relatedQueries: string[];
  duration: number;
  requestId: string;
  contextId: string;
  handleSearch: (
    searchTerm: string,
    searchOrigin: SearchOrigin,
    searchType: SearchType,
    maxRetries?: number,
  ) => Promise<void>;
  products: Product[];
  origin: string;
  contextReceived: boolean;
  suggestedQueries: string[];
  setSuggestedQueries: React.Dispatch<React.SetStateAction<string[]>>;
  setWasSeen: React.Dispatch<React.SetStateAction<boolean>>;
  jsonResponse: string;
  isWaitingForContext: boolean;
  setIsWaitingForContext: React.Dispatch<React.SetStateAction<boolean>>;
  handleProductosPreaprobadosClick: (
    searchOrigin: SearchOrigin,
    searchType: SearchType,
  ) => void;
  hasError: boolean;
  setHasError: React.Dispatch<React.SetStateAction<boolean>>;
  answerComponents: AnswerComponent[];
}

const SearchContext = createContext<SearchContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useSearch = () => {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error("useSearch must be used within a SearchProvider");
  }
  return context;
};

interface SearchProviderProps {
  children: ReactNode;
}

export const SearchProvider: React.FC<SearchProviderProps> = ({ children }) => {
  const [textResponse, setTextResponse] = useState<string>("");
  const [hasSearched, setHasSearched] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isTextResponseComplete, setIsTextResponseComplete] = useState(false);
  const [currentSearchTerm, setCurrentSearchTerm] = useState<string>("");
  const [sources, setSources] = useState<Source[]>([]);
  const [banners, setBanners] = useState<Banner[]>([]);
  const [actions, setActions] = useState<ActionButton[]>([]);
  const [tutorials, setTutorials] = useState<Tutorial[]>([]);
  const [relatedQueries, setRelatedQueries] = useState<string[]>([]);
  const [duration, setDuration] = useState(0);
  const [requestId, setRequestId] = useState<string>("");
  const [contextId, setContextId] = useState<string>("");
  const [wasSeen, setWasSeen] = useState(false);
  const [products, setProducts] = useState<Product[]>([]);
  const [origin, setOrigin] = useState<string>("");
  const [documentType, setDocumentType] = useState<string>("");
  const [contextReceived, setContextReceived] = useState(false);
  const [suggestedQueries, setSuggestedQueries] = useState<string[]>([]);
  const [jsonResponse, setJsonResponse] = useState<string>("{}");
  const [isWaitingForContext, setIsWaitingForContext] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [referrerUrl, setReferrerUrl] = useState<string>("");
  const [answerComponents, setAnswerComponents] = useState<AnswerComponent[]>(
    [],
  );

  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    generateContextId();
  }, []);

  useEffect(() => {
    if (wasSeen && contextId) {
      sendSearchOpened(contextId);
    }
  }, [wasSeen, contextId]);

  useEffect(() => {
    const handlePostMessage = (event: MessageEvent) => {
      if (event.data && typeof event.data === "object") {
        const data: ReceivedMessage = event.data;

        if (data.action === "context:response") {
          setContextReceived(true);
          if (Array.isArray(data.products)) {
            const mappedProducts = data.products.map((product) => ({
              code: product,
              description:
                (productDescriptions as ProductDescription[]).find(
                  (p) => p.code === product,
                )?.description || "Description not available",
            }));

            // If no products are received, add default products based on document type
            if (mappedProducts.length === 0) {
              const defaultProducts = (
                productDescriptions as ProductDescription[]
              )
                .filter(
                  (product) =>
                    product.default &&
                    product.documentTypes.includes(data.documentType),
                )
                .map((product) => ({
                  code: product.code,
                  description: product.description,
                }));

              setProducts(defaultProducts);
              sendContextConfirmation(defaultProducts, data.origin);
            } else {
              setProducts(mappedProducts);
              sendContextConfirmation(mappedProducts, data.origin);
            }
          }

          setOrigin(data.origin);
          setDocumentType(data.documentType);
        } else if (data.action === "referrer" && data.url) {
          setReferrerUrl(data.url);
          console.log("Referrer URL set to:", data.url);
        }
      }
    };

    window.addEventListener("message", handlePostMessage);
    return () => {
      window.removeEventListener("message", handlePostMessage);
    };
  }, [contextId]);

  const generateContextId = () => {
    const id = uuidv4();
    setContextId(id);
  };

  const handleSearch = async (
    searchTerm: string,
    searchOrigin: SearchOrigin,
    searchType: SearchType,
    maxRetries: number = 5,
  ) => {
    if (!searchTerm.length) return;

    // Cancel any ongoing search
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    // Create a new AbortController for this search
    abortControllerRef.current = new AbortController();

    let finalAgentId = "";
    const startTime = Date.now();

    setTextResponse("");
    setIsLoading(true);
    setIsTextResponseComplete(false);
    setSources([]);
    setBanners([]);
    setActions([]);
    setTutorials([]);
    setRelatedQueries([]);
    setRequestId("");
    setHasSearched(true);
    setJsonResponse("{}");
    setHasError(false);

    setCurrentSearchTerm(searchTerm);

    switch (searchType) {
      case "keyword":
        trackAction("search");
        break;
      case "suggestion":
        trackAction("suggestedQuestionClick");
        break;
      case "menu":
        trackAction("cardClick");
        break;
    }

    const sendPostMessage = (requestId: string) => {
      sendSearchClick(
        searchTerm,
        searchOrigin,
        searchType,
        requestId,
        contextId,
        suggestedQueries,
        finalAgentId,
      );
    };
    const metadata = JSON.stringify({
      products,
      documentType,
      referrerUrl,
    });
    let finalTextResponse = "";
    let finalSources: Source[] = [];
    let finalRequestId = "";
    let finalAnswerComponents: AnswerComponent[] = [];
    const mainSearchFunction = async () => {
      if (!abortControllerRef.current) {
        abortControllerRef.current = new AbortController();
      }

      try {
        await PipelineService.ragStream(
          searchTerm,
          (text) => {
            finalTextResponse = text;
            setTextResponse(text);
          },
          (isComplete) => {
            setIsTextResponseComplete(isComplete);
          },
          (sources) => {
            finalSources = sources;
            setSources(sources);
          },
          (relatedQueries) => {
            setRelatedQueries(relatedQueries);
          },
          (answerComponents) => {
            finalAnswerComponents = answerComponents;
            setAnswerComponents(answerComponents);
          },
          (actionButtons) => {
            setActions(actionButtons);
          },
          (tutorials) => {
            setTutorials(tutorials);
          },
          (requestId) => {
            finalRequestId = requestId;
            setRequestId(requestId);

            if (requestId !== "") {
              sendPostMessage(requestId);
            }
          },
          (agentId) => {
            finalAgentId = agentId;
          },
          (fullResponse) => {
            setActions(fullResponse.action_buttons);
            setTutorials(fullResponse.tutorials);
            setRelatedQueries(fullResponse.related_queries);
          },
          (runningJsonResponse) => {
            setJsonResponse(runningJsonResponse);
          },
          contextId,
          metadata,
          abortControllerRef.current.signal,
        );

        // Only set isLoading to false if we successfully completed the search
        setIsLoading(false);
      } catch (error) {
        if (error instanceof Error && error.name === "AbortError") {
          // Don't set isLoading to false if the search was aborted
        } else {
          // For other errors, we should still set isLoading to false
          if (finalTextResponse.length > 0) {
            setHasError(true);
          } else {
            setIsLoading(false);
          }
        }
        throw error;
      }
    };

    const onMaxRetriesReached = () => {
      finalRequestId = "-1";
      setRequestId("-1");
      setHasSearched(false);
    };

    await retry(mainSearchFunction, onMaxRetriesReached, maxRetries, 250);

    const endTime = Date.now();
    const finalDuration = endTime - startTime;
    setDuration(finalDuration);

    abortControllerRef.current = null;

    if (finalRequestId !== "-1") {
      sendSearchResponse(
        searchTerm,
        finalRequestId,
        finalTextResponse,
        finalSources,
        finalDuration,
        contextId,
        products,
        finalAgentId,
        finalAnswerComponents,
      );
    }
  };

  const resetSearch = () => {
    sendCloseClick();
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }

    generateContextId();

    setTextResponse("");
    setHasSearched(false);
    setIsLoading(false);
    setCurrentSearchTerm("");
  };

  const handleProductosPreaprobadosClick = (
    searchOrigin: SearchOrigin,
    searchType: SearchType,
  ) => {
    if (products.length > 0) {
      handleSearch(
        "Quiero conocer mis productos preaprobados",
        searchOrigin,
        searchType,
      );
    } else {
      sendContextRequest();
      setIsWaitingForContext(true);
    }
  };

  const value: SearchContextType = {
    textResponse,
    hasSearched,
    resetSearch,
    isLoading,
    isTextResponseComplete,
    currentSearchTerm,
    sources,
    banners,
    actions,
    tutorials,
    relatedQueries,
    duration,
    requestId,
    contextId,
    handleSearch,
    products,
    origin,
    contextReceived,
    suggestedQueries,
    setSuggestedQueries,
    setWasSeen,
    jsonResponse,
    isWaitingForContext,
    setIsWaitingForContext,
    handleProductosPreaprobadosClick,
    hasError,
    setHasError,
    answerComponents,
  };

  return (
    <SearchContext.Provider value={value}>{children}</SearchContext.Provider>
  );
};

export { SearchContext };
